import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import useHeaders from "../../../hooks/useHeaders";
import { useAppSelector } from "../../../hooks/hooks";
import { ModalSeleccionarHabitacion } from "./ModalSeleccionarHabitacion"; // Asegúrate de importar tu modal de cards

interface Props {
    modalVisible: boolean;
    setModalVisible: (b: boolean) => void;
    fetchLimpiezas: () => void;
}

interface UsuarioStaff {
    id: number;
    nombre: string;
    apellido: string;
}

export const ModalCrearLimpieza = ({
    modalVisible,
    setModalVisible,
    fetchLimpiezas,
}: Props) => {
    const { headers } = useHeaders();
    const house = useAppSelector((state) => state.landing_house.house);

    const [usuarios, setUsuarios] = useState<UsuarioStaff[]>([]);
    const [loading, setLoading] = useState(false);

    // Estados para la habitación seleccionada y el modal de cards
    const [habitacionSelect, setHabitacionSelect] = useState<any>(null);
    const [modalHabitaciones, setModalHabitaciones] = useState(false);

    // Estado para el usuario seleccionado
    const [idUsuario, setIdUsuario] = useState("");

    useEffect(() => {
        if (modalVisible) {
            cargarStaff();
        }
    }, [modalVisible]);

    const cargarStaff = async () => {
        try {
            const resUsers = await axios.get(
                `${process.env.REACT_APP_PROXY}/subusuarios/getAll/Si?tipo_usuario=10`,
                { headers },
            );
            setUsuarios(resUsers.data);
        } catch (error) {
            console.log("Error cargando staff", error);
        }
    };

    const handleSubmit = async () => {
        if (!habitacionSelect || !idUsuario) {
            return Swal.fire(
                "Error",
                "Debes seleccionar habitación y personal",
                "error",
            );
        }

        setLoading(true);
        try {
            const result = await axios.post(
                `${process.env.REACT_APP_PROXY}/limpieza_habitaciones`,
                {
                    id_habitacion: habitacionSelect.id,
                    id_usuario: idUsuario,
                },
                { headers },
            );

           if (result.data.success) {
               Swal.fire({
                   icon: "success",
                   title: "Limpieza Programada",
                   text: "Se ha notificado al personal por correo.",
                   showConfirmButton: false,
                   timer: 2000,
               });

               fetchLimpiezas();
               setModalVisible(false);
               setHabitacionSelect(null);
               setIdUsuario("");
           } else {
               console.log(result.data);
               Swal.fire({
                   icon: "warning",
                   title: "Error al programar limpieza",
                   text:
                       result.data.msg ||
                       "No se pudo crear la tarea de limpieza",
                   showConfirmButton: false,
                   timer: 3000,
               });
           }
        } catch (error) {
            Swal.fire(
                "Error",
                "No se pudo crear la tarea de limpieza",
                "error",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ResponsiveModalAbsolute
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
            >
                <div className="flex flex-col items-center max-w-lg gap-6 p-4 mx-auto md:p-10">
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-gray-800">
                            Programar Limpieza
                        </h2>
                        <p className="text-sm text-gray-500">
                            Asigna staff a una habitación específica
                        </p>
                    </div>

                    <div className="w-full space-y-6">
                        {/* Selector Visual de Habitación */}
                        <div className="flex flex-col gap-2">
                            <label className="ml-1 text-sm font-semibold text-gray-700">
                                Habitación
                            </label>
                            <button
                                onClick={() => setModalHabitaciones(true)}
                                className="flex items-center justify-between w-full p-3 border-2 border-[#CCCCCC] rounded-lg bg-white hover:border-blue-400 transition-colors"
                            >
                                {habitacionSelect ? (
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={
                                                `${process.env.REACT_APP_PROXY}/hotel_habitaciones/getImage/` +
                                                habitacionSelect?.banner
                                            }
                                            className="w-10 h-10 rounded object-cover"
                                            alt=""
                                        />

                                        <span className="font-bold text-blue-600">
                                            {habitacionSelect.nombre}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-gray-400">
                                        Presiona para elegir habitación...
                                    </span>
                                )}
                                <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Selector de Personal */}
                        <div className="flex flex-col gap-2">
                            <label className="ml-1 text-sm font-semibold text-gray-700">
                                Asignar a Personal
                            </label>
                            <select
                                value={idUsuario}
                                onChange={(e) => setIdUsuario(e.target.value)}
                                className="w-full p-3 border-2 border-[#CCCCCC] rounded-lg outline-none focus:border-blue-500 bg-white"
                            >
                                <option value="">
                                    -- Elige un empleado --
                                </option>
                                {usuarios.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.nombre} {u.apellido}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-col w-full gap-4 mt-4">
                        <button
                            disabled={loading}
                            onClick={handleSubmit}
                            className={`p-3 text-white font-bold rounded-lg transition-all ${
                                loading
                                    ? "bg-gray-400"
                                    : "bg-blue-600 hover:bg-blue-700 hover:scale-105"
                            }`}
                        >
                            {loading
                                ? "Programando..."
                                : "Crear Tarea de Limpieza"}
                        </button>
                        <button
                            onClick={() => setModalVisible(false)}
                            className="text-sm font-medium text-gray-500 hover:underline"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </ResponsiveModalAbsolute>

            {/* Modal de selección de habitaciones con Cards */}
            {modalHabitaciones && (
                <ModalSeleccionarHabitacion
                    modalVisible={modalHabitaciones}
                    setModalVisible={setModalHabitaciones}
                    idHotel={house.id}
                    onSelect={(h) => setHabitacionSelect(h)}
                />
            )}
        </>
    );
};
